import { uniqBy } from 'lodash-es';
import { handleActions } from 'redux-actions';
import store from 'store';

import { EmailStatus } from '@components/admin/emails/manuals/enum';
import { ThemeEnum } from '@utils/enums/theme-enum';
import {
  actionTypes as challengeTypes,
  CURRENT_CHALLENGE_KEY
} from '../templates/Challenges/redux/action-types';
import { createAcceptTermsSaga } from './accept-terms-saga';
import { actionTypes, ns as MainApp } from './action-types';
import { createAdminSessionSaga } from './admin-session-saga';
import { createAppMountSaga } from './app-mount-saga';
import { createAuthenticationSaga } from './authentication-saga';
import { createCodeAllySaga } from './codeally-saga';
import { createDiscordSaga } from './discord-saga';
import { createEmailLogsSaga } from './email-logs-saga';
import { createEmailsSaga } from './email-saga';
import { createEmailStatsSaga } from './email-stats-saga';
import failedUpdatesEpic from './failed-updates-epic';
import { createFetchUserSaga } from './fetch-user-saga';
import { createGaSaga } from './ga-saga';
import hardGoToEpic from './hard-go-to-epic';
import { createMessageSaga } from './message-saga';
import { createNacionalSaga } from './nacional-saga';
import { createPlacementTestSaga } from './placement-test-saga';
import { createProductSaga } from './product-saga';
import { createPropsUserSaga } from './props-user-saga';
import { createQuizUserSaga } from './quiz-saga';
import { createReportUserSaga } from './report-user-saga';
import { createSaveChallengeSaga } from './save-challenge-saga';
import { savedChallengesSelector } from './selectors';
import { actionTypes as settingsTypes } from './settings/action-types';
import { createShowCertSaga } from './show-cert-saga';
import { createTourSaga } from './tour-saga';
import updateCompleteEpic from './update-complete-epic';
import { createUserTokenSaga } from './user-token-saga';
import { createUsersDeleteSaga } from './users-delete-saga';

const defaultFetchState = {
  pending: true,
  complete: false,
  errored: false,
  error: null
};

const initialState = {
  appUsername: '',
  recentlyClaimedBlock: null,
  completionCount: 0,
  currentChallengeId: store.get(CURRENT_CHALLENGE_KEY),
  showCert: {},
  showCertFetchState: {
    ...defaultFetchState
  },
  showCodeAlly: false,
  user: {},
  userFetchState: {
    ...defaultFetchState
  },
  allChallengesInfo: {
    challengeEdges: [],
    certificateNodes: []
  },
  userProfileFetchState: {
    ...defaultFetchState
  },
  showSignoutModal: false,
  showModalGoToUnlockPage: false,
  isOnline: true,
  isServerOnline: true,
  codeRegistration: '',
  courseCodeInfo: null,
  userRegistrationStatus: null,
  messageList: [], // for users
  adminMessages: {
    messageList: [],
    messageListSize: null,
    pageNumber: null,
    pageSize: null,
    loading: false
  },
  messageForm: {
    loading: false
  },
  loading: {
    signIn: false,
    signUp: false,
    subscription: false,
    sentEmailToRecovery: false,
    changePassword: false,
    submitAbout: false,
    products: false,
    syncProducts: false,
    unlockUserAccount: false,
    exportDiscordLogs: false
  },
  automaticEmails: {
    emailListSize: null,
    pageNumber: null,
    pageSize: null,
    emailList: [],
    loading: false
  },
  manualEmails: {
    emailListSize: null,
    pageNumber: null,
    pageSize: null,
    emailList: [],
    loading: false
  },
  recurringEmail: {
    emailListSize: null,
    pageNumber: null,
    pageSize: null,
    emailList: [],
    loading: false
  },
  emailStats: {
    dailyMetrics: [],
    loading: false
  },
  emailLogs: {
    data: [],
    pagination: { page: 1, limit: 50, total: 0, totalPages: 0 },
    filters: {},
    loading: false,
    error: null,
    exporting: false
  },
  emailSentQuantity: {
    data: null,
    loading: false
  },
  showEmailDrawer: false,
  quizUser: {
    data: null,
    loading: false,
    nationalResponse: {
      id: '',
      ra: '',
      name: '',
      cpf: '',
      birthDate: '',
      sex: '',
      homeCountry: '',
      homeState: '',
      homeCity: '',
      email: '',
      educationLevel: '',
      race: '',
      country: '',
      nationality: '',
      state: '',
      city: '',
      phone: '',
      cel: '',
      occupationalSituation: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      cep: '',
      successFetchingUser: false
    }
  },
  userManagement: {
    userListSize: null,
    pageNumber: null,
    pageSize: null,
    usersList: [],
    loading: false
  },
  showEditUserDrawer: false,
  preEnrolled: {
    preEnrolledListSize: null,
    pageNumber: null,
    pageSize: null,
    preEnrolledList: [],
    loading: false
  },
  requestedToDeleteUsers: {
    result: [],
    loading: false,
    availableSources: [],
    dataCount: 0
  },
  deletedUsers: {
    result: [],
    loading: false,
    availableSources: [],
    dataCount: 0
  },
  quizAddress: {
    loading: false,
    address: {},
    cities: []
  },
  products: [],
  unlockModuleModal: {
    show: false,
    superBlock: ''
  },
  classManagement: {
    classGroupsListSize: null,
    pageNumber: null,
    pageSize: null,
    clasGroupssList: [],
    classGroupsSummary: [],
    loading: false
  },
  classGroupUsers: {
    users: [],
    totalUsers: 0,
    pageNumber: 1,
    pageSize: 50,
    loading: false,
    error: null
  },
  editClassGroup: {
    loading: false
  },
  moveUsersBetweenClassGroups: {
    loading: false
  },
  placementTestQuestions: {
    loading: false,
    questions: []
  },
  waitingList: {
    waitingList: [],
    loading: false,
    availableGroups: [],
    dataCount: 0
  },
  reprovedAndDropoutList: {
    reprovedAndDropoutList: [],
    loading: false,
    availableGroups: [],
    availableStatuses: [],
    dataCount: 0
  },
  confirmedList: {
    confirmedList: [],
    loading: false,
    availableGroups: [],
    availableSources: [],
    dataCount: 0
  },
  waitingToConfirmList: {
    waitingToConfirmList: [],
    loading: false,
    availableGroups: [],
    availableSources: [],
    dataCount: 0
  },
  groups: {
    list: [],
    loading: false
  },
  theme: {
    currentTheme: ThemeEnum.LIGTH
  },
  faq: {
    faqList: [],
    categories: [],
    loading: false
  },
  discord: {
    discordUser: null,
    loading: false
  },
  discordRole: {
    data: null,
    loading: false,
    error: null
  },
  discordLogs: {
    discordLogs: [],
    dataCount: 0,
    availableSources: [],
    availableRoles: [],
    availableStatuses: [],
    loading: false
  },
  discordLogHistory: {
    logs: [],
    dataCount: 0,
    loading: false
  },
  enrollStudent: {
    loading: false
  },
  markDropout: {
    loading: false
  },
  classGroupsByGroup: {
    list: [],
    loading: false
  },
  enrollmentAuditLogs: {
    list: [],
    loading: false,
    error: false
  }
};

export const epics = [hardGoToEpic, failedUpdatesEpic, updateCompleteEpic];

export const sagas = [
  ...createAcceptTermsSaga(actionTypes),
  ...createAppMountSaga(actionTypes),
  ...createCodeAllySaga(actionTypes),
  ...createGaSaga(actionTypes),
  ...createFetchUserSaga(actionTypes),
  ...createShowCertSaga(actionTypes),
  ...createReportUserSaga(actionTypes),
  ...createUserTokenSaga(actionTypes),
  ...createSaveChallengeSaga(actionTypes),
  ...createAuthenticationSaga(actionTypes),
  ...createPropsUserSaga(actionTypes),
  ...createTourSaga(actionTypes),
  ...createNacionalSaga(actionTypes),
  ...createMessageSaga(actionTypes),
  ...createEmailsSaga(actionTypes),
  ...createQuizUserSaga(actionTypes),
  ...createAdminSessionSaga(actionTypes),
  ...createUsersDeleteSaga(actionTypes),
  ...createProductSaga(actionTypes),
  ...createPlacementTestSaga(actionTypes),
  ...createEmailStatsSaga(actionTypes),
  ...createEmailLogsSaga(actionTypes),
  ...createDiscordSaga(actionTypes)
];

function spreadThePayloadOnUser(state, payload) {
  return {
    ...state,
    user: {
      ...state.user,
      [state.appUsername]: {
        ...state.user[state.appUsername],
        ...payload
      }
    }
  };
}

export const reducer = handleActions(
  {
    [actionTypes.acceptTermsComplete]: (state, { payload }) => {
      const { appUsername } = state;
      return {
        ...state,
        user: {
          ...state.user,
          [appUsername]: {
            ...state.user[appUsername],
            // TODO: the user accepts the privacy terms in practice during auth
            // however, it's currently being used to track if they've accepted
            // or rejected the newsletter. Ideally this should be migrated,
            // since they can't sign up without accepting the terms.
            acceptedPrivacyTerms: true,
            sendQuincyEmail:
              payload === null
                ? state.user[appUsername].sendQuincyEmail
                : payload
          }
        }
      };
    },
    [actionTypes.updateAllChallengesInfo]: (state, { payload }) => ({
      ...state,
      allChallengesInfo: { ...payload }
    }),
    [actionTypes.codeRegistration]: (state, { payload }) => ({
      ...state,
      codeRegistration: payload
    }),
    [actionTypes.fetchMessagesComplete]: (state, { payload }) => ({
      ...state,
      messageList: payload
    }),
    [actionTypes.fetchMessagesForAdmin]: state => ({
      ...state,
      adminMessages: {
        ...state.adminMessages,
        loading: true
      }
    }),
    [actionTypes.fetchMessagesForAdminComplete]: (state, { payload }) => ({
      ...state,
      adminMessages: {
        ...state.adminMessages,
        ...payload,
        loading: false
      }
    }),
    [actionTypes.createMessage]: state => ({
      ...state,
      messageForm: {
        ...state.messageForm,
        loading: true
      }
    }),
    [actionTypes.createMessageComplete]: state => ({
      ...state,
      messageForm: {
        ...state.messageForm,
        loading: false
      }
    }),
    [actionTypes.createMessageError]: state => ({
      ...state,
      messageForm: {
        ...state.messageForm,
        loading: false
      }
    }),
    [actionTypes.updateMessage]: state => ({
      ...state,
      messageForm: {
        ...state.messageForm,
        loading: true
      }
    }),
    [actionTypes.updateMessageComplete]: state => ({
      ...state,
      messageForm: {
        ...state.messageForm,
        loading: false
      }
    }),
    [actionTypes.updateMessageError]: state => ({
      ...state,
      messageForm: {
        ...state.messageForm,
        loading: false
      }
    }),
    [actionTypes.fetchUser]: state => ({
      ...state,
      userFetchState: { ...defaultFetchState }
    }),
    [actionTypes.fetchProfileForUser]: state => ({
      ...state,
      userProfileFetchState: { ...defaultFetchState }
    }),
    [actionTypes.fetchUserComplete]: (
      state,
      { payload: { user, username } }
    ) => ({
      ...state,
      user: {
        ...state.user,
        [username]: { ...user, sessionUser: true }
      },
      appUsername: username,
      currentChallengeId: user.currentChallengeId,
      userFetchState: {
        pending: false,
        complete: true,
        errored: false,
        error: null
      }
    }),
    [actionTypes.fetchUserError]: (state, { payload }) => ({
      ...state,
      userFetchState: {
        pending: false,
        complete: false,
        errored: true,
        error: payload
      }
    }),
    [actionTypes.addUserAiAssistantSessions]: (state, { payload }) => ({
      ...state,
      user: {
        ...state.user,
        [state.appUsername]: {
          ...state.user[state.appUsername],
          aiAssistantSessions: [
            ...state.user[state.appUsername].aiAssistantSessions,
            payload
          ]
        }
      }
    }),
    [actionTypes.fetchProfileForUserComplete]: (
      state,
      { payload: { user, username } }
    ) => {
      const previousUserObject =
        username in state.user ? state.user[username] : {};
      return {
        ...state,
        user: {
          ...state.user,
          [username]: { ...previousUserObject, ...user }
        },
        userProfileFetchState: {
          ...defaultFetchState,
          pending: false,
          complete: true
        }
      };
    },
    [actionTypes.fetchProfileForUserError]: (state, { payload }) => ({
      ...state,
      userProfileFetchState: {
        pending: false,
        complete: false,
        errored: true,
        error: payload
      }
    }),
    [actionTypes.onlineStatusChange]: (state, { payload: isOnline }) => ({
      ...state,
      isOnline
    }),
    [actionTypes.serverStatusChange]: (state, { payload: isServerOnline }) => ({
      ...state,
      isServerOnline
    }),
    [actionTypes.resetUserData]: state => ({
      ...state,
      appUsername: '',
      user: {}
    }),
    [actionTypes.openSignoutModal]: state => ({
      ...state,
      showSignoutModal: true
    }),
    [actionTypes.closeSignoutModal]: state => ({
      ...state,
      showSignoutModal: false
    }),
    [actionTypes.openModalGoToUnlockPage]: state => ({
      ...state,
      showModalGoToUnlockPage: true
    }),
    [actionTypes.closeModalGoToUnlockPage]: state => ({
      ...state,
      showModalGoToUnlockPage: false
    }),
    [actionTypes.showCert]: state => ({
      ...state,
      showCert: {},
      showCertFetchState: { ...defaultFetchState }
    }),
    [actionTypes.showCertComplete]: (state, { payload }) => ({
      ...state,
      showCert: payload,
      showCertFetchState: {
        ...defaultFetchState,
        pending: false,
        complete: true
      }
    }),
    [actionTypes.showCertError]: (state, { payload }) => ({
      ...state,
      showCert: {},
      showCertFetchState: {
        pending: false,
        complete: false,
        errored: true,
        error: payload
      }
    }),
    [actionTypes.submitComplete]: (state, { payload }) => {
      const { submittedChallenge, savedChallenges } = payload;
      let submittedchallenges = [
        { ...submittedChallenge, completedDate: Date.now() }
      ];
      if (submittedChallenge.challArray) {
        submittedchallenges = submittedChallenge.challArray;
      }
      const { appUsername } = state;
      return {
        ...state,
        completionCount: state.completionCount + 1,
        user: {
          ...state.user,
          [appUsername]: {
            ...state.user[appUsername],
            completedChallenges: uniqBy(
              [
                ...submittedchallenges,
                ...state.user[appUsername].completedChallenges
              ],
              'id'
            ),
            savedChallenges:
              savedChallenges ?? savedChallengesSelector(state[MainApp])
          }
        }
      };
    },
    [actionTypes.updateUserToken]: (state, { payload }) => {
      const { appUsername } = state;
      return {
        ...state,
        user: {
          ...state.user,
          [appUsername]: {
            ...state.user[appUsername],
            userToken: payload
          }
        }
      };
    },
    [actionTypes.deleteUserTokenComplete]: state => {
      const { appUsername } = state;
      return {
        ...state,
        user: {
          ...state.user,
          [appUsername]: {
            ...state.user[appUsername],
            userToken: null
          }
        }
      };
    },
    [actionTypes.hideCodeAlly]: state => {
      return {
        ...state,
        showCodeAlly: false
      };
    },
    [actionTypes.showCodeAlly]: state => {
      return {
        ...state,
        showCodeAlly: true
      };
    },
    [challengeTypes.challengeMounted]: (state, { payload }) => ({
      ...state,
      currentChallengeId: payload
    }),
    [actionTypes.saveChallengeComplete]: (state, { payload }) => {
      const { appUsername } = state;
      return {
        ...state,
        user: {
          ...state.user,
          [appUsername]: {
            ...state.user[appUsername],
            savedChallenges: payload
          }
        }
      };
    },
    [actionTypes.startLoading]: (state, { payload }) => ({
      ...state,
      loading: {
        ...state.loading,
        [payload]: true
      }
    }),
    [actionTypes.stopLoading]: (state, { payload }) => ({
      ...state,
      loading: {
        ...state.loading,
        [payload]: false
      }
    }),
    [actionTypes.fetchAutomaticEmails]: state => ({
      ...state,
      automaticEmails: {
        ...state.automaticEmails,
        loading: true
      }
    }),
    [actionTypes.fetchAutomaticEmailsComplete]: (state, { payload }) => ({
      ...state,
      automaticEmails: {
        ...state.automaticEmails,
        ...payload,
        loading: false
      }
    }),
    [actionTypes.toggleActiveAutomaticEmails]: state => ({
      ...state,
      automaticEmails: {
        ...state.automaticEmails,
        loading: true
      }
    }),
    [actionTypes.toggleActiveAutomaticEmailComplete]: (state, { payload }) => ({
      ...state,
      automaticEmails: {
        ...state.automaticEmails,
        emailList: state.automaticEmails.emailList.map(email =>
          email.id === payload.id
            ? { ...email, isActive: payload.isActive }
            : email
        ),
        loading: false
      }
    }),
    [actionTypes.editAutomaticEmail]: state => ({
      ...state,
      automaticEmails: {
        ...state.automaticEmails,
        loading: true
      }
    }),
    [actionTypes.editAutomaticEmailComplete]: (state, { payload }) => {
      return {
        ...state,
        automaticEmails: {
          ...state.automaticEmails,
          emailList: state.automaticEmails.emailList.map(email =>
            email.id === payload.id ? { ...email, ...payload.data } : email
          ),
          loading: false
        }
      };
    },
    [actionTypes.fetchManualEmails]: state => ({
      ...state,
      manualEmails: {
        ...state.manualEmails,
        loading: true
      }
    }),
    [actionTypes.fetchManualEmailsComplete]: (state, { payload }) => ({
      ...state,
      manualEmails: {
        ...state.manualEmails,
        ...payload,
        loading: false
      }
    }),
    [actionTypes.createManualEmail]: state => ({
      ...state,
      manualEmails: {
        ...state.manualEmails,
        loading: true
      }
    }),
    [actionTypes.createManualEmailComplete]: state => {
      return {
        ...state,
        manualEmails: {
          ...state.manualEmails,
          loading: false
        }
      };
    },
    [actionTypes.createManualEmailError]: state => {
      return {
        ...state,
        manualEmails: {
          ...state.manualEmails,
          loading: false
        }
      };
    },
    [actionTypes.editManualEmail]: state => ({
      ...state,
      manualEmails: {
        ...state.manualEmails,
        loading: true
      }
    }),
    [actionTypes.editManualEmailComplete]: (state, { payload }) => {
      const emailList = state.manualEmails.emailList.map(email =>
        email.id === payload.id ? { ...email, ...payload } : email
      );

      return {
        ...state,
        manualEmails: {
          ...state.manualEmails,
          emailList,
          loading: false
        }
      };
    },
    [actionTypes.cancelManualEmail]: state => ({
      ...state,
      manualEmails: {
        ...state.manualEmails,
        loading: true
      }
    }),
    [actionTypes.cancelManualEmailComplete]: (state, { payload }) => ({
      ...state,
      manualEmails: {
        ...state.manualEmails,
        emailList: state.manualEmails.emailList.map(email =>
          email.id === payload.id
            ? { ...email, isActive: false, status: EmailStatus.CANCELLED }
            : email
        ),
        loading: false
      }
    }),
    [actionTypes.fetchRecurringEmails]: state => ({
      ...state,
      recurringEmail: {
        ...state.recurringEmail,
        loading: true
      }
    }),
    [actionTypes.fetchRecurringEmailsComplete]: (state, { payload }) => ({
      ...state,
      recurringEmail: {
        ...state.recurringEmail,
        ...payload,
        loading: false
      }
    }),
    [actionTypes.createRecurringEmail]: state => ({
      ...state,
      recurringEmail: {
        ...state.recurringEmail,
        loading: true
      }
    }),
    [actionTypes.createRecurringEmailComplete]: state => {
      return {
        ...state,
        recurringEmail: {
          ...state.recurringEmail,
          loading: false
        }
      };
    },
    [actionTypes.editRecurringEmail]: state => ({
      ...state,
      recurringEmail: {
        ...state.recurringEmail,
        loading: true
      }
    }),
    [actionTypes.editRecurringEmailComplete]: (state, { payload }) => {
      const emailList = state.recurringEmail.emailList.map(email =>
        email.id === payload.id ? { ...email, ...payload } : email
      );

      return {
        ...state,
        recurringEmail: {
          ...state.recurringEmail,
          emailList,
          loading: false
        }
      };
    },
    [actionTypes.toggleActiveRecurringEmail]: state => ({
      ...state,
      recurringEmail: {
        ...state.recurringEmail,
        loading: true
      }
    }),
    [actionTypes.toggleActiveRecurringEmailComplete]: (state, { payload }) => {
      const { appUsername, user, recurringEmail } = state;

      const updatedEmailList = recurringEmail.emailList.map(email =>
        email.id === payload.id
          ? {
              ...email,
              isActive: payload.isActive,
              changeLog: [
                ...email.changeLog,
                {
                  id: Date.now(),
                  date: new Date().toISOString(),
                  author: user[appUsername].name
                }
              ]
            }
          : email
      );

      return {
        ...state,
        recurringEmail: {
          ...recurringEmail,
          emailList: updatedEmailList,
          loading: false
        }
      };
    },
    [actionTypes.setLoadingRecurringEmails]: (state, { payload }) => ({
      ...state,
      recurringEmail: {
        ...state.recurringEmail,
        loading: payload
      }
    }),
    [actionTypes.openEmailDrawer]: state => ({
      ...state,
      showEmailDrawer: true
    }),
    [actionTypes.closeEmailDrawer]: state => ({
      ...state,
      showEmailDrawer: false
    }),
    [actionTypes.registerQuizUser]: state => ({
      ...state,
      quizUser: {
        ...state.quizUser,
        loading: true
      }
    }),
    [actionTypes.registerQuizUserComplete]: (state, { payload }) => ({
      ...state,
      quizUser: {
        data: payload?.user || null,
        nationalResponse: {
          ...state.quizUser.nationalResponse,
          ...(payload?.national || {})
        },
        loading: false
      }
    }),
    [actionTypes.updateNationalUser]: (state, { payload }) => ({
      ...state,
      quizUser: {
        ...state.quizUser,
        nationalResponse: {
          ...state.quizUser.nationalResponse,
          ...payload
        }
      }
    }),
    [actionTypes.updateQuizUserResult]: state => ({
      ...state,
      quizUser: {
        ...state.quizUser,
        loading: true
      }
    }),
    [actionTypes.updateQuizUserResultComplete]: (state, { payload }) => ({
      ...state,
      quizUser: {
        ...state.quizUser,
        nationalResponse: {
          ...state.quizUser.nationalResponse
        },
        data: payload,
        loading: false
      }
    }),
    [actionTypes.toggleReceiveEmails]: state => ({
      ...state,
      quizUser: {
        ...state.quizUser,
        loading: true
      }
    }),
    [actionTypes.toggleReceiveEmailsComplete]: (state, { payload }) => ({
      ...state,
      quizUser: {
        data: payload,
        loading: false
      }
    }),
    [actionTypes.createNewUserFromQuiz]: state => ({
      ...state,
      quizUser: {
        ...state.quizUser,
        loading: true
      }
    }),
    [actionTypes.createNewUserFromQuizComplete]: (state, { payload }) => ({
      ...state,
      quizUser: {
        data: payload,
        loading: false
      }
    }),
    [actionTypes.createNewUserFromQuizError]: state => ({
      ...state,
      quizUser: {
        ...state.quizUser,
        loading: false
      }
    }),
    [actionTypes.createNewNationalUser]: state => ({
      ...state,
      quizUser: {
        ...state.quizUser,
        loading: true
      }
    }),
    [actionTypes.createNewNationalUserComplete]: (state, { payload }) => ({
      ...state,
      quizUser: {
        ...state.quizUser,
        loading: false,
        nationalResponse: {
          ...state.quizUser.nationalResponse,
          ...payload
        }
      }
    }),
    [actionTypes.createNewNationalUserError]: state => ({
      ...state,
      quizUser: {
        ...state.quizUser,
        loading: false
      }
    }),
    [actionTypes.setLoadingChangePassword]: (state, { payload }) => ({
      ...state,
      loading: {
        ...state.loading,
        changePassword: payload
      }
    }),
    [actionTypes.fetchAllUsers]: state => ({
      ...state,
      userManagement: {
        ...state.userManagement,
        loading: true
      }
    }),
    [actionTypes.fetchAllUsersComplete]: (state, { payload }) => ({
      ...state,
      userManagement: {
        ...state.userManagement,
        ...payload,
        loading: false
      }
    }),
    [actionTypes.fetchEditUser]: state => ({
      ...state,
      userManagement: {
        ...state.userManagement,
        loading: true
      }
    }),
    [actionTypes.fetchEditUserComplete]: state => ({
      ...state,
      userManagement: {
        ...state.userManagement,
        loading: false
      }
    }),
    [actionTypes.openEditUserDrawer]: state => ({
      ...state,
      showEditUserDrawer: true
    }),
    [actionTypes.closeEditUserDrawer]: state => ({
      ...state,
      showEditUserDrawer: false
    }),
    [actionTypes.fetchAllPreEnrolled]: state => ({
      ...state,
      preEnrolled: {
        ...state.preEnrolled,
        loading: true
      }
    }),
    [actionTypes.fetchAllPreEnrolledComplete]: (state, { payload }) => ({
      ...state,
      preEnrolled: {
        ...state.preEnrolled,
        ...payload,
        loading: false
      }
    }),
    [actionTypes.getCep]: state => ({
      ...state,
      quizAddress: {
        ...state.quizAddress,
        address: {},
        loading: true
      }
    }),
    [actionTypes.getCepComplete]: (state, { payload }) => ({
      ...state,
      quizAddress: {
        ...state.quizAddress,
        address: payload,
        loading: false
      }
    }),
    [actionTypes.getCityByState]: state => ({
      ...state,
      quizAddress: {
        ...state.quizAddress,
        cities: [],
        loading: true
      }
    }),
    [actionTypes.getCityByStateComplete]: (state, { payload }) => ({
      ...state,
      quizAddress: {
        ...state.quizAddress,
        cities: payload,
        loading: false
      }
    }),
    [actionTypes.setProducts]: (state, { payload }) => ({
      ...state,
      products: payload
    }),
    [actionTypes.setLoadingProducts]: (state, { payload }) => ({
      ...state,
      loading: {
        ...state.loading,
        products: payload
      }
    }),
    [actionTypes.setLoadingSyncProducts]: (state, { payload }) => ({
      ...state,
      loading: {
        ...state.loading,
        syncProducts: payload
      }
    }),
    [actionTypes.openUnlockModuleModal]: state => ({
      ...state,
      unlockModuleModal: {
        ...state.unlockModuleModal,
        show: true
      }
    }),
    [actionTypes.closeUnlockModuleModal]: state => ({
      ...state,
      unlockModuleModal: {
        ...state.unlockModuleModal,
        show: false
      }
    }),
    [actionTypes.updateSuperBlockUnlockModuleModal]: (state, { payload }) => ({
      ...state,
      unlockModuleModal: {
        ...state.unlockModuleModal,
        superBlock: payload
      }
    }),
    [settingsTypes.submitNewUsernameComplete]: (state, { payload }) =>
      payload
        ? {
            ...state,
            user: {
              ...state.user,
              [state.appUsername]: {
                ...state.user[state.appUsername],
                username: payload
              }
            }
          }
        : state,
    [settingsTypes.submitNewAbout]: state => ({
      ...state,
      loading: {
        ...state.loading,
        submitAbout: true
      }
    }),
    [settingsTypes.submitNewAboutComplete]: (state, { payload }) => {
      if (state.user[state.appUsername].access.includes('nacional')) {
        return {
          ...state,
          user: {
            ...state.user,
            [state.appUsername]: {
              ...state.user[state.appUsername],
              nationalDataComplement: {
                ...state.user[state.appUsername].nationalDataComplement,
                ...payload
              }
            }
          },
          loading: {
            ...state.loading,
            submitAbout: false
          }
        };
      }

      return payload ? spreadThePayloadOnUser(state, payload) : state;
    },
    [settingsTypes.submitNewAboutError]: state => ({
      ...state,
      loading: {
        ...state.loading,
        submitAbout: false
      }
    }),
    [settingsTypes.updateMyEmailComplete]: (state, { payload }) =>
      payload ? spreadThePayloadOnUser(state, payload) : state,
    [settingsTypes.updateMySocialsComplete]: (state, { payload }) =>
      payload ? spreadThePayloadOnUser(state, payload) : state,
    [settingsTypes.updateMySoundComplete]: (state, { payload }) =>
      payload ? spreadThePayloadOnUser(state, payload) : state,
    [settingsTypes.updateMyThemeComplete]: (state, { payload }) =>
      payload ? spreadThePayloadOnUser(state, payload) : state,
    [settingsTypes.updateMyKeyboardShortcutsComplete]: (state, { payload }) =>
      payload ? spreadThePayloadOnUser(state, payload) : state,
    [settingsTypes.updateMyHonestyComplete]: (state, { payload }) =>
      payload ? spreadThePayloadOnUser(state, payload) : state,
    [settingsTypes.updateMyQuincyEmailComplete]: (state, { payload }) =>
      payload ? spreadThePayloadOnUser(state, payload) : state,
    [settingsTypes.updateMyPortfolioComplete]: (state, { payload }) =>
      payload ? spreadThePayloadOnUser(state, payload) : state,
    [settingsTypes.verifyCertComplete]: (state, { payload }) =>
      payload ? spreadThePayloadOnUser(state, payload) : state,
    [settingsTypes.submitProfileUIComplete]: (state, { payload }) =>
      payload
        ? {
            ...state,
            user: {
              ...state.user,
              [state.appUsername]: {
                ...state.user[state.appUsername],
                profileUI: { ...payload }
              }
            }
          }
        : state,

    [actionTypes.fetchRequestToDeleteUsers]: state => ({
      ...state,
      requestedToDeleteUsers: {
        ...state.requestedToDeleteUsers,
        loading: true
      }
    }),
    [actionTypes.fetchRequestToDeleteUsersComplete]: (state, { payload }) => ({
      ...state,
      requestedToDeleteUsers: {
        ...state.requestedToDeleteUsers,
        result: payload.result,
        availableSources: payload.availableSources,
        dataCount: payload.dataCount,
        loading: false
      }
    }),
    [actionTypes.fetchDeletedUsers]: state => ({
      ...state,
      deletedUsers: {
        ...state.deletedUsers,
        loading: true
      }
    }),
    [actionTypes.fetchDeletedUsersComplete]: (state, { payload }) => ({
      ...state,
      deletedUsers: {
        ...state.deletedUsers,
        result: payload.result,
        availableSources: payload.availableSources,
        dataCount: payload.dataCount,
        loading: false
      }
    }),
    [actionTypes.fetchAllClassGroups]: state => ({
      ...state,
      classManagement: {
        ...state.classManagement,
        loading: true
      }
    }),
    [actionTypes.fetchAllClassGroupsComplete]: (state, { payload }) => ({
      ...state,
      classManagement: {
        ...state.classManagement,
        ...payload,
        loading: false
      }
    }),
    [actionTypes.fetchClassGroupUsers]: state => ({
      ...state,
      classGroupUsers: {
        ...state.classGroupUsers,
        loading: true,
        error: null
      }
    }),
    [actionTypes.fetchClassGroupUsersComplete]: (state, { payload }) => ({
      ...state,
      classGroupUsers: {
        ...state.classGroupUsers,
        users: payload.users || [],
        totalUsers: payload.totalUsers || 0,
        pageNumber: payload.pageNumber || 1,
        pageSize: payload.pageSize || 50,
        loading: false,
        error: null
      }
    }),
    [actionTypes.fetchClassGroupUsersError]: (state, { payload }) => ({
      ...state,
      classGroupUsers: {
        ...state.classGroupUsers,
        loading: false,
        error: payload.error || 'Erro ao buscar usuários'
      }
    }),
    [actionTypes.editClassGroup]: state => ({
      ...state,
      editClassGroup: {
        ...state.editClassGroup,
        loading: true
      }
    }),
    [actionTypes.editClassGroupComplete]: state => ({
      ...state,
      editClassGroup: {
        ...state.editClassGroup,
        loading: false
      }
    }),
    [actionTypes.editClassGroupError]: state => ({
      ...state,
      editClassGroup: {
        ...state.editClassGroup,
        loading: false
      }
    }),
    [actionTypes.moveUsersBetweenClassGroups]: state => ({
      ...state,
      moveUsersBetweenClassGroups: {
        ...state.moveUsersBetweenClassGroups,
        loading: true
      }
    }),
    [actionTypes.moveUsersBetweenClassGroupsComplete]: state => ({
      ...state,
      moveUsersBetweenClassGroups: {
        ...state.moveUsersBetweenClassGroups,
        loading: false
      }
    }),
    [actionTypes.moveUsersBetweenClassGroupsError]: state => ({
      ...state,
      moveUsersBetweenClassGroups: {
        ...state.moveUsersBetweenClassGroups,
        loading: false
      }
    }),
    [actionTypes.completeNacionalRegister]: state => ({
      ...state,
      quizAddress: {
        ...state.quizAddress,
        address: {},
        loading: true
      }
    }),
    [actionTypes.completeNacionalRegisterComplete]: state => ({
      ...state,
      quizAddress: {
        ...state.quizAddress,
        loading: false
      }
    }),
    [actionTypes.getPlacementTestQuestions]: state => ({
      ...state,
      placementTestQuestions: {
        ...state.placementTestQuestions,
        loading: true
      }
    }),
    [actionTypes.getPlacementTestQuestionsComplete]: (state, { payload }) => ({
      ...state,
      placementTestQuestions: {
        ...state.placementTestQuestions,
        loading: false,
        questions: payload.questions
      }
    }),
    [actionTypes.postPlacementTestCorrection]: state => ({
      ...state,
      placementTestQuestions: {
        ...state.placementTestQuestions,
        loading: true
      }
    }),
    [actionTypes.postPlacementTestCorrectionComplete]: state => ({
      ...state,
      placementTestQuestions: {
        ...state.placementTestQuestions,
        questions: [],
        loading: false
      }
    }),
    [actionTypes.fetchWaitingList]: state => ({
      ...state,
      waitingList: {
        ...state.waitingList,
        loading: true
      }
    }),
    [actionTypes.fetchWaitingListComplete]: (state, { payload }) => ({
      ...state,
      waitingList: {
        ...state.waitingList,
        ...payload,
        loading: false
      }
    }),
    [actionTypes.fetchWaitingListError]: state => ({
      ...state,
      waitingList: {
        ...state.waitingList,
        loading: false
      }
    }),
    [actionTypes.fetchReprovedAndDropoutList]: state => ({
      ...state,
      reprovedAndDropoutList: {
        ...state.reprovedAndDropoutList,
        loading: true
      }
    }),
    [actionTypes.fetchReprovedAndDropoutListComplete]: (
      state,
      { payload }
    ) => ({
      ...state,
      reprovedAndDropoutList: {
        ...state.reprovedAndDropoutList,
        ...payload,
        loading: false
      }
    }),
    [actionTypes.fetchReprovedAndDropoutListError]: state => ({
      ...state,
      reprovedAndDropoutList: {
        ...state.reprovedAndDropoutList,
        loading: false
      }
    }),
    [actionTypes.fetchConfirmedList]: state => ({
      ...state,
      confirmedList: {
        ...state.confirmedList,
        loading: true
      }
    }),
    [actionTypes.fetchConfirmedListComplete]: (state, { payload }) => ({
      ...state,
      confirmedList: {
        ...state.confirmedList,
        ...payload,
        loading: false
      }
    }),
    [actionTypes.fetchConfirmedListError]: state => ({
      ...state,
      confirmedList: {
        ...state.confirmedList,
        loading: false
      }
    }),
    [actionTypes.fetchWaitingToConfirmList]: state => ({
      ...state,
      waitingToConfirmList: {
        ...state.waitingToConfirmList,
        loading: true
      }
    }),
    [actionTypes.fetchWaitingToConfirmListComplete]: (state, { payload }) => ({
      ...state,
      waitingToConfirmList: {
        ...state.waitingToConfirmList,
        ...payload,
        loading: false
      }
    }),
    [actionTypes.fetchWaitingToConfirmListError]: state => ({
      ...state,
      waitingToConfirmList: {
        ...state.waitingToConfirmList,
        loading: false
      }
    }),
    [actionTypes.fetchAllGroups]: state => ({
      ...state,
      groups: {
        ...state.groups,
        loading: true
      }
    }),
    [actionTypes.fetchAllGroupsComplete]: (state, { payload }) => ({
      ...state,
      groups: {
        ...state.groups,
        ...payload,
        loading: false
      }
    }),
    // Email Stats Reducers
    [actionTypes.fetchEmailStats]: state => ({
      ...state,
      emailStats: {
        ...state.emailStats,
        loading: true
      }
    }),
    [actionTypes.fetchEmailStatsComplete]: (state, { payload }) => ({
      ...state,
      emailStats: {
        ...state.emailStats,
        dailyMetrics: payload,
        loading: false
      }
    }),
    [actionTypes.fetchEmailStatsError]: state => ({
      ...state,
      emailStats: {
        ...state.emailStats,
        loading: false
      }
    }),
    [actionTypes.fetchEmailSentQuantity]: state => ({
      ...state,
      emailSentQuantity: {
        ...state.emailSentQuantity,
        loading: true
      }
    }),
    [actionTypes.fetchEmailSentQuantityComplete]: (state, { payload }) => ({
      ...state,
      emailSentQuantity: {
        ...state.emailSentQuantity,
        data: payload,
        loading: false
      }
    }),
    [actionTypes.fetchEmailSentQuantityError]: state => ({
      ...state,
      emailSentQuantity: {
        ...state.emailSentQuantity,
        loading: false
      }
    }),
    [actionTypes.updateEmailStats]: state => ({
      ...state,
      emailStats: {
        ...state.emailStats,
        loading: true
      }
    }),
    [actionTypes.updateEmailStatsComplete]: state => ({
      ...state,
      emailStats: {
        ...state.emailStats,
        loading: false
      }
    }),
    [actionTypes.updateEmailStatsError]: state => ({
      ...state,
      emailStats: {
        ...state.emailStats,
        loading: false
      }
    }),
    // Email Logs Reducers
    [actionTypes.fetchEmailLogs]: (state, { payload }) => ({
      ...state,
      emailLogs: {
        ...state.emailLogs,
        filters: payload || {},
        loading: true,
        error: null
      }
    }),
    [actionTypes.fetchEmailLogsComplete]: (state, { payload }) => ({
      ...state,
      emailLogs: {
        ...state.emailLogs,
        data: payload.data,
        pagination: payload.pagination,
        loading: false
      }
    }),
    [actionTypes.fetchEmailLogsError]: (state, { payload }) => ({
      ...state,
      emailLogs: {
        ...state.emailLogs,
        loading: false,
        error: payload
      }
    }),
    [actionTypes.exportEmailLogs]: state => ({
      ...state,
      emailLogs: {
        ...state.emailLogs,
        exporting: true
      }
    }),
    [actionTypes.exportEmailLogsComplete]: state => ({
      ...state,
      emailLogs: {
        ...state.emailLogs,
        exporting: false
      }
    }),
    [actionTypes.exportEmailLogsError]: state => ({
      ...state,
      emailLogs: {
        ...state.emailLogs,
        exporting: false
      }
    }),
    [actionTypes.setCurrentTheme]: (state, { payload }) => ({
      ...state,
      theme: {
        ...state.theme,
        currentTheme: payload
      }
    }),
    /* FAQ Reducers */
    [actionTypes.fetchAllFAQs]: state => ({
      ...state,
      faq: {
        ...state.faq,
        loading: true
      }
    }),
    [actionTypes.fetchAllFAQsComplete]: (state, { payload }) => ({
      ...state,
      faq: {
        ...state.faq,
        ...payload,
        loading: false
      }
    }),
    [actionTypes.fetchAllFAQsError]: state => ({
      ...state,
      faq: {
        ...state.faq,
        loading: false
      }
    }),
    [actionTypes.fetchPublicFAQs]: state => ({
      ...state,
      faq: {
        ...state.faq,
        loading: true
      }
    }),
    [actionTypes.fetchPublicFAQsComplete]: (state, { payload }) => ({
      ...state,
      faq: {
        ...state.faq,
        publicFaqList: payload.faqList || [],
        publicCategories: payload.categories || [],
        loading: false
      }
    }),
    [actionTypes.fetchPublicFAQsError]: state => ({
      ...state,
      faq: {
        ...state.faq,
        loading: false
      }
    }),
    [actionTypes.createFAQ]: state => ({
      ...state,
      faq: {
        ...state.faq,
        loading: true
      }
    }),
    [actionTypes.createFAQComplete]: state => ({
      ...state,
      faq: {
        ...state.faq,
        loading: false
      }
    }),
    [actionTypes.createFAQError]: state => ({
      ...state,
      faq: {
        ...state.faq,
        loading: false
      }
    }),
    [actionTypes.updateFAQ]: state => ({
      ...state,
      faq: {
        ...state.faq,
        loading: true
      }
    }),
    [actionTypes.updateFAQComplete]: state => ({
      ...state,
      faq: {
        ...state.faq,
        loading: false
      }
    }),
    [actionTypes.updateFAQError]: state => ({
      ...state,
      faq: {
        ...state.faq,
        loading: false
      }
    }),
    [actionTypes.removeFAQ]: state => ({
      ...state,
      faq: {
        ...state.faq,
        loading: true
      }
    }),
    [actionTypes.removeFAQComplete]: state => ({
      ...state,
      faq: {
        ...state.faq,
        loading: false
      }
    }),
    [actionTypes.removeFAQError]: state => ({
      ...state,
      faq: {
        ...state.faq,
        loading: false
      }
    }),
    // Discord Reducers
    [actionTypes.fetchDiscordUser]: state => ({
      ...state,
      discord: {
        ...state.discord,
        loading: true
      }
    }),
    [actionTypes.fetchDiscordUserComplete]: (state, { payload }) => ({
      ...state,
      discord: {
        ...state.discord,
        discordUser: payload,
        loading: false
      }
    }),
    [actionTypes.fetchDiscordUserError]: state => ({
      ...state,
      discord: {
        ...state.discord,
        loading: false
      }
    }),
    // Discord Logs Reducers
    [actionTypes.fetchDiscordLogs]: state => ({
      ...state,
      discordLogs: {
        ...state.discordLogs,
        loading: true
      }
    }),
    [actionTypes.fetchDiscordLogsComplete]: (state, { payload }) => ({
      ...state,
      discordLogs: {
        ...state.discordLogs,
        discordLogs: payload.discordLogs,
        dataCount: payload.dataCount,
        availableSources: payload.availableSources,
        availableRoles: payload.availableRoles,
        loading: false
      }
    }),
    [actionTypes.fetchDiscordLogsError]: state => ({
      ...state,
      discordLogs: {
        ...state.discordLogs,
        loading: false
      }
    }),
    [actionTypes.fetchDiscordLogHistory]: state => ({
      ...state,
      discordLogHistory: {
        ...state.discordLogHistory,
        loading: true
      }
    }),
    [actionTypes.fetchDiscordLogHistoryComplete]: (state, { payload }) => ({
      ...state,
      discordLogHistory: {
        logs: payload.logs,
        dataCount: payload.dataCount,
        loading: false
      }
    }),
    [actionTypes.fetchDiscordLogHistoryError]: state => ({
      ...state,
      discordLogHistory: {
        ...state.discordLogHistory,
        loading: false
      }
    }),
    [actionTypes.exportDiscordLogs]: state => ({
      ...state,
      loading: {
        ...state.loading,
        exportDiscordLogs: true
      }
    }),
    [actionTypes.exportDiscordLogsComplete]: state => ({
      ...state,
      loading: {
        ...state.loading,
        exportDiscordLogs: false
      }
    }),
    [actionTypes.exportDiscordLogsError]: state => ({
      ...state,
      loading: {
        ...state.loading,
        exportDiscordLogs: false
      }
    }),
    // Discord Role Reducers
    [actionTypes.fetchDiscordRole]: state => ({
      ...state,
      discordRole: {
        ...state.discordRole,
        loading: true,
        error: null
      }
    }),
    [actionTypes.fetchDiscordRoleComplete]: (state, { payload }) => ({
      ...state,
      discordRole: {
        data: payload,
        loading: false,
        error: null
      }
    }),
    [actionTypes.fetchDiscordRoleError]: (state, { payload }) => ({
      ...state,
      discordRole: {
        data: null,
        loading: false,
        error: payload
      }
    }),
    /* Enrollment Management Reducers */
    [actionTypes.enrollStudent]: state => ({
      ...state,
      enrollStudent: {
        ...state.enrollStudent,
        loading: true
      }
    }),
    [actionTypes.enrollStudentComplete]: state => ({
      ...state,
      enrollStudent: {
        ...state.enrollStudent,
        loading: false
      }
    }),
    [actionTypes.enrollStudentError]: state => ({
      ...state,
      enrollStudent: {
        ...state.enrollStudent,
        loading: false
      }
    }),
    [actionTypes.markDropout]: state => ({
      ...state,
      markDropout: {
        ...state.markDropout,
        loading: true
      }
    }),
    [actionTypes.markDropoutComplete]: state => ({
      ...state,
      markDropout: {
        ...state.markDropout,
        loading: false
      }
    }),
    [actionTypes.markDropoutError]: state => ({
      ...state,
      markDropout: {
        ...state.markDropout,
        loading: false
      }
    }),
    [actionTypes.fetchClassGroupsByGroup]: state => ({
      ...state,
      classGroupsByGroup: {
        ...state.classGroupsByGroup,
        loading: true
      }
    }),
    [actionTypes.fetchClassGroupsByGroupComplete]: (state, { payload }) => ({
      ...state,
      classGroupsByGroup: {
        ...state.classGroupsByGroup,
        list: payload,
        loading: false
      }
    }),
    [actionTypes.fetchClassGroupsByGroupError]: state => ({
      ...state,
      classGroupsByGroup: {
        ...state.classGroupsByGroup,
        loading: false
      }
    }),
    /* Enrollment Audit Logs Reducers */
    [actionTypes.fetchEnrollmentAuditLogs]: state => ({
      ...state,
      enrollmentAuditLogs: {
        ...state.enrollmentAuditLogs,
        loading: true,
        error: false
      }
    }),
    [actionTypes.fetchEnrollmentAuditLogsComplete]: (state, { payload }) => ({
      ...state,
      enrollmentAuditLogs: {
        ...state.enrollmentAuditLogs,
        list: payload,
        loading: false
      }
    }),
    [actionTypes.fetchEnrollmentAuditLogsError]: state => ({
      ...state,
      enrollmentAuditLogs: {
        ...state.enrollmentAuditLogs,
        error: true,
        loading: false
      }
    })
  },
  initialState
);
